import { Facility } from 'interfaces/facility';
import { FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ReactQueryKeys } from 'constants/react-query-keys';
import FacilityService from 'services/FacilityService';
import { CARE_AREA_QUERY_PARAM, FACILITY_QUERY_PARAM } from 'constants/device';
import { useDispatch } from 'react-redux';
import { deviceActions } from 'store/slices/device';
import useBlockerExceptionalCases from 'hooks/useBlockerExceptionalCases';

/**
 * Props for the Facilities component
 * @typedef {Object} FacilitiesProps
 * @property {boolean} canManage - Whether the user has permission to manage facilities
 */
type FacilitiesProps = {
  canManage: boolean;
};

/**
 * @description Facilities component that displays a list of facilities and handles facility selection.
 * Fetches facilities data, manages the selected facility state, and provides UI for users to select different facilities.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {FacilitiesProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const Facilities: FC<FacilitiesProps> = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const handleBlock = useBlockerExceptionalCases();

  const { data: facilities } = useQuery({
    queryKey: [ReactQueryKeys.FACILITIES],

    queryFn: () =>
      FacilityService.get({
        pageIndex: 0,
        pageSize: 1000,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
      }),

    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  /**
   * @description Updates the selected facility in URL params and Redux store
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Facility} facility - The facility to select
   *
   * @returns {void}
   */
  const updateSelectedFacility = (facility: Facility) => {
    searchParams.set(FACILITY_QUERY_PARAM, String(facility.id));
    setSearchParams(searchParams);

    dispatch(
      deviceActions.updateSelectedFacility({
        facility,
      }),
    );
  };

  useEffect(() => {
    if (facilities?.data.records.length) {
      if (!searchParams.has(FACILITY_QUERY_PARAM)) {
        updateSelectedFacility(facilities.data.records[0]);
      } else {
        const selectedFacility = facilities.data.records.find(
          (facility: any) =>
            Number(facility.id) ===
            Number(searchParams.get(FACILITY_QUERY_PARAM)),
        );

        if (selectedFacility) {
          updateSelectedFacility(selectedFacility);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilities, searchParams]);

  /**
   * @description Handles user clicking on a facility.
   * Checks if navigation should be blocked, updates the selected facility, and clears related selections in the Redux store.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {Facility} facility - The facility that was clicked
   *
   * @returns {Promise<void>}
   */
  const handleFacilityClick = async (facility: Facility) => {
    const canWeContinue = await handleBlock(() => true);

    if (!canWeContinue) return;
    updateSelectedFacility(facility);

    searchParams.delete(CARE_AREA_QUERY_PARAM);
    setSearchParams(searchParams);
    dispatch(deviceActions.clearSelectedCareArea());
    dispatch(deviceActions.clearAddingCareArea());
    dispatch(deviceActions.clearSelectedDrug());
    dispatch(deviceActions.clearSelectedDrugConcentration());
  };

  return (
    <>
      <h4
        className="device__facility-title pb-1"
        data-testid="facility-component"
      >
        {t('device.facilities')}
      </h4>
      {facilities?.data?.records && facilities.data.records?.length === 0 && (
        <div className="alert mb-0">{t('messages.no_records')}</div>
      )}

      {facilities?.data?.records && facilities.data.records?.length > 0 && (
        <div className="master-list device__facility--master-list">
          {facilities?.data.records.map((facility: Facility) => {
            return (
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  handleFacilityClick(facility);
                }}
                className={`master-list__item device-item ${
                  searchParams.get(FACILITY_QUERY_PARAM) === String(facility.id)
                    ? 'active'
                    : ''
                }`}
                key={facility.id}
              >
                {facility.name}
              </a>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Facilities;
